import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { DASHBOARD_VISUALIZATION_TYPE } from './dashboardContract';
import {
  buildVisualizationChartData,
  visualizationHasData
} from './dashboardViewModel';

const SERIES_COLOURS = [
  '#D94B4B',
  '#3E7CB1',
  '#4D9078',
  '#7B5EA7',
  '#C47F17',
  '#2D8C9E'
];

const DashboardVisualizationChart = ({ visualization, seriesMetrics }) => {
  const chartData = buildVisualizationChartData(visualization, seriesMetrics);
  const orderedSeries = [...(visualization.series ?? [])].sort(
    (left, right) => left.seriesOrder - right.seriesOrder
  );
  const hasRightAxis = orderedSeries.some((series) => series.yAxis === 'right');
  const Chart =
    visualization.visualizationType === DASHBOARD_VISUALIZATION_TYPE.BAR
      ? BarChart
      : LineChart;

  return (
    <Paper
      data-testid={`dashboard-visualization-${visualization.dashboardVisualizationId}`}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        minWidth: 0,
        p: { xs: 2, md: 2.5 }
      }}
    >
      <Typography component="h2" fontSize={18} fontWeight={700}>
        {visualization.name}
      </Typography>
      {visualization.description && (
        <Typography color="text.secondary" fontSize={13} sx={{ mt: 0.5 }}>
          {visualization.description}
        </Typography>
      )}

      {!visualizationHasData(visualization, chartData) ? (
        <Box
          sx={{
            alignItems: 'center',
            color: 'text.secondary',
            display: 'flex',
            height: 320,
            justifyContent: 'center'
          }}
        >
          <Typography>No approved data in this date range.</Typography>
        </Box>
      ) : (
        <Box sx={{ height: 360, mt: 2, width: '100%' }}>
          <ResponsiveContainer height="100%" width="100%">
            <Chart
              data={chartData}
              margin={{ bottom: 25, left: 10, right: 15, top: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="effectiveDate"
                label={
                  visualization.xAxisLabel
                    ? {
                        offset: -16,
                        position: 'insideBottom',
                        value: visualization.xAxisLabel
                      }
                    : undefined
                }
                minTickGap={24}
                tickFormatter={(value) => dayjs(value).format('MMM D')}
              />
              <YAxis
                yAxisId="left"
                label={
                  visualization.leftYAxisLabel
                    ? {
                        angle: -90,
                        position: 'insideLeft',
                        value: visualization.leftYAxisLabel
                      }
                    : undefined
                }
              />
              {hasRightAxis && (
                <YAxis
                  orientation="right"
                  yAxisId="right"
                  label={
                    visualization.rightYAxisLabel
                      ? {
                          angle: 90,
                          position: 'insideRight',
                          value: visualization.rightYAxisLabel
                        }
                      : undefined
                  }
                />
              )}
              <ChartTooltip
                labelFormatter={(value) => dayjs(value).format('MMM D, YYYY')}
              />
              {visualization.showLegend && <Legend verticalAlign="top" />}
              {orderedSeries.map((series, index) => {
                const colour =
                  series.colour ||
                  SERIES_COLOURS[index % SERIES_COLOURS.length];
                const commonProps = {
                  dataKey: series.metricDefinitionId,
                  name: series.label,
                  yAxisId: series.yAxis
                };

                return visualization.visualizationType ===
                  DASHBOARD_VISUALIZATION_TYPE.BAR ? (
                  <Bar
                    {...commonProps}
                    fill={colour}
                    key={series.metricDefinitionId}
                    maxBarSize={48}
                  />
                ) : (
                  <Line
                    {...commonProps}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                    dot={false}
                    key={series.metricDefinitionId}
                    stroke={colour}
                    strokeWidth={2.5}
                    type="monotone"
                  />
                );
              })}
            </Chart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default DashboardVisualizationChart;
